import { Template } from '../constants/templates';
import { ReactRenderer } from '@atlaskit/renderer';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { N40 } from '@atlaskit/theme/colors';
import { getEmojiProvider } from '../providers/emoji/get-emoji-provider';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { mentionResourceProvider } from '../providers/mentions/sandbox-mention-resource';
import Spinner from '@atlaskit/spinner';
import { RefObject, useEffect, useRef } from 'react';

interface TemplatePreviewProps {
  templates: Template[];
  currentTemplateIndex?: number;
}

const centeredContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40vw;
  height: 85vh;
  min-width: 480px;
  max-width: 600px;
  min-height: 540px;
  max-height: 790px;
`;

const previewContainer = css`
  display: flex;
  flex-direction: column;

  width: 40vw;
  height: 85vh;
  min-width: 480px;
  max-width: 600px;
  min-height: 540px;
  max-height: 790px;

  border-radius: ${token('border.radius', '3px')};
  background-color: ${token('elevation.surface.overlay')};
  box-shadow: ${token('elevation.shadow.overlay')};
`;

const previewHeading = css`
  display: flex;
  height: 53px;
  padding: 14px ${token('space.250', '20px')};
  flex-shrink: 0;
  flex-grow: 0;
  box-sizing: border-box;
  align-items: center;
  border-bottom: 1px solid ${token('color.border', N40)};
  gap: ${token('space.100', '8px')};
`;

const previewTemplateName = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const renderContainer = css`
  height: calc(100% - 53px);
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
`;

const renderHeightContainer = css`
  height: 100%;
`;

const renderScrollContainer = css`
  transform: scale(0.65);
  transform-origin: 0 0;
  width: ${100 / 0.65}%;
  height: ${100 / 0.65}%;
  padding: ${token('space.250', '20px')} 0;
  overflow-y: auto;
  box-sizing: border-box;

  .pm-table-container {
    width: auto !important;
    left: auto !important;

    colgroup {
      display: none !important;
    }
  }

  .fabric-editor-breakout-mark {
    width: 100% !important;
  }
`;

const pointerEventsNone = css`
  user-select: none;
  pointer-events: none;
`;

const providerFactory = ProviderFactory.create({
  emojiProvider: getEmojiProvider(),
  mentionProvider: Promise.resolve(mentionResourceProvider)
});

export const TemplatePreview = ({
  templates,
  currentTemplateIndex = -1
}: TemplatePreviewProps) => {
  const currentTemplate = templates[currentTemplateIndex];
  const scrollRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [currentTemplateIndex]);

  if (!currentTemplate) {
    return (
      <div css={centeredContainer}>
        <Spinner size="medium" testId="template-preview-spinner" />
      </div>
    );
  }

  return (
    <div css={previewContainer}>
      <div css={previewHeading}>
        <img
          width={24}
          height={24}
          src={currentTemplate.icon}
          alt={currentTemplate.name}
        />
        <div css={previewTemplateName}>{currentTemplate.name}</div>
      </div>
      <div css={renderContainer}>
        <div css={renderHeightContainer}>
          <div
            ref={scrollRef as RefObject<HTMLDivElement>}
            css={renderScrollContainer}
          >
            <div css={pointerEventsNone}>
              <ReactRenderer
                appearance="full-page"
                adfStage="stage0"
                allowColumnSorting={false}
                enableSsrInlineScripts={false}
                allowHeadingAnchorLinks={false}
                stickyHeaders={undefined}
                allowPlaceholderText
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                document={currentTemplate.template as any}
                dataProviders={providerFactory}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
